import { useApplyEdition } from '@/pages/Apply/components/use-apply-edition';
import { router } from '@inertiajs/react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toastFlash } from '@/lib/flash-toast';
import { errorMessages } from './errors';

interface Props {
  viewerRole: 'leader' | 'member';
  /** Leader only: leaving hands leadership to the oldest member. */
  willTransfer: boolean;
  /** Leader name shown to a member ("salir del equipo de @X"). */
  leaderHandle?: string;
}

export function LeaveTeamButton({
  viewerRole,
  willTransfer,
  leaderHandle,
}: Props) {
  const { eventSlug, eventCity } = useApplyEdition();
  const [isPending, setIsPending] = useState(false);

  const isCancel = viewerRole === 'leader' && !willTransfer;

  const triggerLabel = isCancel
    ? 'Cancelar mi postulación'
    : 'Salir del equipo';

  const title = isCancel ? '¿Cancelar tu postulación?' : '¿Salir del equipo?';

  const description = isCancel
    ? `Se cancelará tu postulación a Platanus Hack 26: ${eventCity}. Podrás volver a postular o unirte a otro equipo después.`
    : willTransfer
      ? 'El liderazgo del equipo pasará al miembro más antiguo y tú quedarás fuera. Podrás unirte a otro equipo después.'
      : `Vas a salir del equipo${
          leaderHandle ? ` de @${leaderHandle}` : ''
        }. Tu lugar quedará libre y podrás unirte a otro equipo después.`;

  const leave = () => {
    setIsPending(true);
    router.delete(`/${eventSlug}/apply/team`, {
      onSuccess: (page) => toastFlash(page, 'Saliste del equipo'),
      onError: (errors) =>
        toast.error(errorMessages(errors).base ?? 'No se pudo salir'),
      onFinish: () => setIsPending(false),
    });
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          disabled={isPending}
          className="text-destructive text-xs underline-offset-4 hover:underline disabled:opacity-50"
        >
          {triggerLabel}
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Volver</AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={leave}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isCancel ? 'Cancelar postulación' : 'Salir'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
